import Phaser from 'phaser';
import {
  ComponentType,
  ComponentCategory,
  COMPONENT_DEFS,
} from '@/components/ComponentDefs';
import { SoundManager } from '@/systems/SoundManager';
import { createComponentVisual } from '@/systems/ComponentIcons';
import type { GridManager } from './GridManager';

// ── Edge connectivity helpers ──
// Edge indices: 0=top, 1=right, 2=bottom, 3=left
// Rotation shifts edges clockwise: 90° CW means top→right, right→bottom, etc.

/** Get rotated allowedEdges for a component at given rotation (radians). */
export function getRotatedEdges(type: ComponentType, rotation: number): [boolean, boolean, boolean, boolean] {
  const base = COMPONENT_DEFS[type].allowedEdges;
  // Convert rotation to number of 90° steps clockwise (handle negatives)
  const steps = (((Math.round(rotation / (Math.PI / 2)) % 4) + 4) % 4);
  if (steps === 0) return [...base];
  // Rotate: each step shifts all edges clockwise
  // step 1 (90° CW): [top,right,bottom,left] → [left,top,right,bottom]
  const out: [boolean, boolean, boolean, boolean] = [...base];
  for (let s = 0; s < steps; s++) {
    const prev: [boolean, boolean, boolean, boolean] = [...out];
    out[0] = prev[3]; // top ← left
    out[1] = prev[0]; // right ← top
    out[2] = prev[1]; // bottom ← right
    out[3] = prev[2]; // left ← bottom
  }
  return out;
}

/** Get final edges for a component considering rotation AND flip state. */
export function getTransformedEdges(
  type: ComponentType, rotation: number, flipX: boolean, flipY: boolean,
): [boolean, boolean, boolean, boolean] {
  const edges = getRotatedEdges(type, rotation);
  // flipX (horizontal mirror): swap left ↔ right
  if (flipX) { const tmp = edges[1]; edges[1] = edges[3]; edges[3] = tmp; }
  // flipY (vertical mirror): swap top ↔ bottom
  if (flipY) { const tmp = edges[0]; edges[0] = edges[2]; edges[2] = tmp; }
  return edges;
}

/**
 * Check if two adjacent cells have compatible edges.
 * direction: which side of cell A faces cell B
 * 0=B is above A (A's top ↔ B's bottom)
 * 1=B is right of A (A's right ↔ B's left)
 * 2=B is below A (A's bottom ↔ B's top)
 * 3=B is left of A (A's left ↔ B's right)
 */
export function edgesCompatible(
  typeA: ComponentType, rotA: number,
  typeB: ComponentType, rotB: number,
  direction: number, // 0=up, 1=right, 2=down, 3=left (from A's perspective)
  flipAx = false, flipAy = false, flipBx = false, flipBy = false,
): boolean {
  const edgesA = getTransformedEdges(typeA, rotA, flipAx, flipAy);
  const edgesB = getTransformedEdges(typeB, rotB, flipBx, flipBy);
  const opposite = (direction + 2) % 4;
  return edgesA[direction] && edgesB[opposite];
}

/** Get forbidden edges after rotation + flip (same transform as allowedEdges). */
export function getTransformedForbiddenEdges(
  type: ComponentType, rotation: number, flipX: boolean, flipY: boolean,
): [boolean, boolean, boolean, boolean] {
  const base = COMPONENT_DEFS[type].forbiddenEdges;
  const steps = (((Math.round(rotation / (Math.PI / 2)) % 4) + 4) % 4);
  const out: [boolean, boolean, boolean, boolean] = [...base];
  for (let s = 0; s < steps; s++) {
    const prev: [boolean, boolean, boolean, boolean] = [...out];
    out[0] = prev[3]; out[1] = prev[0]; out[2] = prev[1]; out[3] = prev[2];
  }
  if (flipX) { const tmp = out[1]; out[1] = out[3]; out[3] = tmp; }
  if (flipY) { const tmp = out[0]; out[0] = out[2]; out[2] = tmp; }
  return out;
}

/** Types that are mutually exclusive with STAB_LEGS */
const MOBILITY_TYPES = new Set([
  ComponentType.WHEEL, ComponentType.WING, ComponentType.THRUSTER, ComponentType.TURBINE,
]);

export interface PlacementCallbacks {
  recalcStats: () => void;
  refreshRuleDisplays: () => void;
}

export class PlacementValidator {
  // Grid drag state (move placed component)
  gridDragSource: { r: number; c: number } | null = null;
  gridDragType: ComponentType | null = null;
  gridDragRotation = 0;
  gridDragEvoLevel = 0;
  gridDragFlipX = false;
  gridDragFlipY = false;
  gridDragGhost: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite | null = null;
  gridDragStartTime = 0;
  gridDragActive = false;
  flipModeActive = false;
  flipToggleBtn: Phaser.GameObjects.Container | null = null;

  constructor(
    private scene: Phaser.Scene,
    private gridManager: GridManager,
    private callbacks: PlacementCallbacks,
  ) {}

  setup(): void {
    // No event listeners needed — PlacementValidator is called imperatively
  }

  destroy(): void {
    if (this.gridDragGhost) {
      this.gridDragGhost.destroy();
      this.gridDragGhost = null;
    }
  }

  /** Check if placing type at (row,col) with given rotation has at least one valid edge connection. */
  hasValidEdgeConnection(row: number, col: number, type: ComponentType, rotation: number): boolean {
    const { rows, cols } = this.gridManager.getDimensions();
    // First component on the grid — always valid
    let hasAnyOther = false;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.gridManager.getCell(r, c) !== null && (r !== row || c !== col)) { hasAnyOther = true; break; }
      }
      if (hasAnyOther) break;
    }
    if (!hasAnyOther) return true;

    // Check 4 neighbors for at least one compatible edge
    const dirs: [number, number, number][] = [[-1, 0, 0], [0, 1, 1], [1, 0, 2], [0, -1, 3]]; // [dr, dc, edgeDir]
    for (const [dr, dc, dir] of dirs) {
      const nr = row + dr;
      const nc = col + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      const neighborType = this.gridManager.getCell(nr, nc);
      if (neighborType === null) continue;
      if (edgesCompatible(type, rotation, neighborType, this.gridManager.getRotation(nr, nc), dir,
        this.gridManager.getFlipX(row, col), this.gridManager.getFlipY(row, col),
        this.gridManager.getFlipX(nr, nc), this.gridManager.getFlipY(nr, nc))) {
        return true;
      }
    }
    return false;
  }

  /** Check if placing a component at (row, col) with given rotation would violate any forbidden edge. */
  hasForbiddenConflict(row: number, col: number, type: ComponentType, rotation: number): boolean {
    const { rows, cols } = this.gridManager.getDimensions();
    const forbidden = getTransformedForbiddenEdges(
      type, rotation, this.gridManager.getFlipX(row, col), this.gridManager.getFlipY(row, col));
    const dirs: [number, number, number][] = [[-1, 0, 0], [0, 1, 1], [1, 0, 2], [0, -1, 3]];
    for (const [dr, dc, dir] of dirs) {
      if (!forbidden[dir]) continue;
      const nr = row + dr, nc = col + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (this.gridManager.getCell(nr, nc) !== null) return true; // occupied where forbidden
    }
    return false;
  }

  screenToGrid(sx: number, sy: number): { r: number; c: number } | null {
    return this.gridManager.screenToGrid(sx, sy);
  }

  getTypeFromPaletteRect(rect: Phaser.GameObjects.Rectangle, paletteHighlights: Map<string, Phaser.GameObjects.Rectangle>): { type: ComponentType; evoLevel: number } | null {
    for (const [key, bg] of paletteHighlights) {
      if (bg === rect) {
        const [typeStr, lvlStr] = key.split(':');
        return { type: typeStr as ComponentType, evoLevel: parseInt(lvlStr, 10) };
      }
    }
    return null;
  }

  /** Check if the current build has any WHEEL component */
  hasWheelsOnGrid(): boolean {
    const { rows, cols } = this.gridManager.getDimensions();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.gridManager.getCell(r, c) === ComponentType.WHEEL) return true;
      }
    }
    return false;
  }

  /** Returns true if grid has WING or TURBINE (flight-capable) */
  hasFlightOnGrid(): boolean {
    const { rows, cols } = this.gridManager.getDimensions();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const t = this.gridManager.getCell(r, c);
        if (t === ComponentType.WING || t === ComponentType.TURBINE) return true;
      }
    }
    return false;
  }

  /** Ground robots: Direction restricted to left/right. Flyers: any direction */
  isDirectionAllowed(rotation: number): boolean {
    if (this.hasFlightOnGrid()) return true; // flyers: any direction
    const norm = ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const eps = 0.01;
    return norm < eps || Math.abs(norm - Math.PI) < eps || Math.abs(norm - Math.PI * 2) < eps;
  }

  /** Rotate the component at grid[r][c] by +90°, or flip if in flip mode. */
  rotateOnGrid(r: number, c: number): void {
    const type = this.gridManager.getCell(r, c)!;

    // If flip mode active, cycle flips instead of rotating
    if (this.flipModeActive) {
      this.flipOnGrid(r, c);
      return;
    }

    let newRot = (this.gridManager.getRotation(r, c) + Math.PI / 2) % (Math.PI * 2);

    // Ground robot (no wings/turbines) + DIRECTION: skip vertical rotations
    if (type === ComponentType.DIRECTION && !this.hasFlightOnGrid()) {
      // Only left/right: 0 → π → 0 → ...
      const norm = ((newRot % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const eps = 0.1;
      if (!(norm < eps || Math.abs(norm - Math.PI) < eps || Math.abs(norm - Math.PI * 2) < eps)) {
        newRot = (this.gridManager.getRotation(r, c) + Math.PI) % (Math.PI * 2);
      }
    }

    this.gridManager.setRotation(r, c, newRot);
    this.gridManager.redrawCell(r, c);
    this.callbacks.recalcStats();
  }

  /** Cycle flip state: (none) → flipX → flipY → (none) */
  flipOnGrid(r: number, c: number): void {
    const fx = this.gridManager.getFlipX(r, c);
    const fy = this.gridManager.getFlipY(r, c);
    if (!fx && !fy) {
      this.gridManager.setFlipX(r, c, true);
      this.gridManager.setFlipY(r, c, false);
    } else if (fx && !fy) {
      this.gridManager.setFlipX(r, c, false);
      this.gridManager.setFlipY(r, c, true);
    } else {
      this.gridManager.setFlipX(r, c, false);
      this.gridManager.setFlipY(r, c, false);
    }
    this.gridManager.redrawCell(r, c);
    this.callbacks.recalcStats();
  }

  placeOnGrid(row: number, col: number, type: ComponentType, rotation = 0, skipValidation = false, skipCost = false, evoLevel = 0): boolean {
    const { rows, cols } = this.gridManager.getDimensions();

    // Stab Legs ↔ mobility exclusion: cannot coexist on same robot
    if (type === ComponentType.STAB_LEGS || MOBILITY_TYPES.has(type)) {
      const isPlacingLegs = type === ComponentType.STAB_LEGS;
      const conflictSet = isPlacingLegs ? MOBILITY_TYPES : new Set([ComponentType.STAB_LEGS]);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const ct = this.gridManager.getCell(r, c);
          if (ct !== null && conflictSet.has(ct)) {
            const conflictName = isPlacingLegs ? 'mobility components' : 'Stab. Legs';
            this.gridManager.showQuickToast(`Cannot combine with ${conflictName}!`);
            return false;
          }
        }
      }
    }

    // Wheel adjacency: wheels can only attach to non-wheel components
    if (!skipValidation && type === ComponentType.WHEEL) {
      const dirs: [number, number][] = [[-1, 0], [0, 1], [1, 0], [0, -1]];
      let hasNonWheelNeighbor = false;
      let hasAnyNeighbor = false;
      for (const [dr, dc] of dirs) {
        const nr = row + dr;
        const nc = col + dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        const neighborType = this.gridManager.getCell(nr, nc);
        if (neighborType === null) continue;
        hasAnyNeighbor = true;
        if (neighborType !== ComponentType.WHEEL) hasNonWheelNeighbor = true;
      }
      if (hasAnyNeighbor && !hasNonWheelNeighbor) {
        this.gridManager.showQuickToast('Wheels must attach to a structure!');
        return false;
      }
    }

    // Stab Legs: only one per robot — remove any existing one first
    if (type === ComponentType.STAB_LEGS) {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.gridManager.getCell(r, c) === ComponentType.STAB_LEGS && (r !== row || c !== col)) {
            this.gridManager.removeFromGrid(r, c, this.callbacks);
          }
        }
      }
    }

    // Direction component: only one per robot — remove any existing one first
    if (type === ComponentType.DIRECTION) {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.gridManager.getCell(r, c) === ComponentType.DIRECTION && (r !== row || c !== col)) {
            this.gridManager.removeFromGrid(r, c, this.callbacks);
          }
        }
      }
      if (!skipValidation && !this.isDirectionAllowed(rotation)) {
        rotation = 0;
      }
    }

    // Edge validation: try all 4 rotations to find the best-connecting one.
    if (!skipValidation) {
      if (!this.hasValidEdgeConnection(row, col, type, rotation) || this.hasForbiddenConflict(row, col, type, rotation)) {
        for (let step = 1; step <= 3; step++) {
          const tryRot = rotation + step * (Math.PI / 2);
          if (this.hasValidEdgeConnection(row, col, type, tryRot) && !this.hasForbiddenConflict(row, col, type, tryRot)) {
            rotation = tryRot;
            break;
          }
        }
        if (this.hasForbiddenConflict(row, col, type, rotation)) {
          for (let step = 1; step <= 3; step++) {
            const tryRot = rotation + step * (Math.PI / 2);
            if (!this.hasForbiddenConflict(row, col, type, tryRot)) {
              rotation = tryRot;
              break;
            }
          }
        }
      }
    }

    // Remove existing component in this cell
    if (this.gridManager.getCell(row, col) !== null) {
      this.gridManager.removeFromGrid(row, col, this.callbacks);
    }

    this.gridManager.placeComponent(row, col, type, rotation, evoLevel);

    if (!this.gridManager.batchMode) {
      // Placement sparkle effect
      const { cellSize, gridX, gridY } = this.gridManager.getLayout();
      const cx = gridX + col * cellSize + cellSize / 2;
      const cy = gridY + row * cellSize + cellSize / 2;
      const sparkle = this.scene.add.circle(cx, cy, cellSize / 3, 0x4488cc, 0.3).setDepth(6);
      this.scene.tweens.add({
        targets: sparkle, alpha: 0, scaleX: 2, scaleY: 2,
        duration: 250, onComplete: () => sparkle.destroy(),
      });

      this.callbacks.recalcStats();
      this.gridManager.drawConnections();
      this.callbacks.refreshRuleDisplays();
    }
    return true;
  }

  /** Begin dragging a placed component from the grid */
  startGridDrag(): void {
    if (!this.gridDragSource || !this.gridDragType) return;
    this.gridDragActive = true;

    const { r, c } = this.gridDragSource;

    // Remove from grid visually (keep data in gridDrag fields)
    this.gridManager.removeFromGrid(r, c, this.callbacks);

    // Create ghost following cursor
    const def = COMPONENT_DEFS[this.gridDragType];
    const { cellSize } = this.gridManager.getLayout();
    const gridGhost = createComponentVisual(this.scene, this.gridDragType, cellSize, def.color, this.gridDragEvoLevel);
    (gridGhost as Phaser.GameObjects.Graphics).setDepth(50).setAlpha(0.6);
    this.gridManager.applyGridRotation(gridGhost, this.gridDragType, this.gridDragRotation, this.gridDragEvoLevel);
    if (this.gridDragFlipX) gridGhost.scaleX *= -1;
    if (this.gridDragFlipY) gridGhost.scaleY *= -1;
    this.gridDragGhost = gridGhost;

    const pointer = this.scene.input.activePointer;
    gridGhost.setPosition(pointer.x, pointer.y);
  }

  /** Auto-assign rotation (radians) for directional components */
  autoRotation(type: ComponentType): number {
    switch (type) {
      case ComponentType.THRUSTER: return Math.PI;
      case ComponentType.CANNON_LIGHT:
      case ComponentType.CANNON_HEAVY:
      case ComponentType.BURST_CANNON:
      case ComponentType.MISSILE_SOL_SOL: return 0;
      default: return 0;
    }
  }
}
